"use client";

import { useState, useEffect } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { TotpMultiFactorGenerator, getMultiFactorResolver, sendPasswordResetEmail, signInWithEmailAndPassword, type MultiFactorError, type MultiFactorResolver } from "firebase/auth";
import { FirebaseError } from "firebase/app";
import { useTranslations } from "next-intl";
import { useAuth } from "@/lib/auth/AuthContext";
import { isSuperAdmin } from "@/lib/auth/user";
import { sanitizeRedirectPath } from "@/lib/auth/safe-redirect";
import { MFA_ERROR_CODES, isFirebaseError, isTotpCode } from "@/lib/auth/mfa-client";
import { signInWithGoogle, signInWithMicrosoft } from "@/components/AuthProvider";
import { signInWithSSO } from "@/lib/auth/saml";
import { lookupSsoProvider } from "@/app/actions/auth/lookup-sso-provider";
import { auth } from "@/lib/firebase/client";
import { KeyRound } from "lucide-react";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { LegalFooter } from "@/components/LegalFooter";
import { AuthShell, AuthLoadingScreen } from "@/components/auth/AuthShell";
import { TotpCodeInput } from "@/components/auth/mfa/totp-code-input";

// Accounts are provisioned by AI Reserve (no self-serve signup), so the only
// "no account" path from this page is the sales/onboarding inbox.
const ACCOUNT_REQUEST_HREF = "mailto:product@aireserve.com?subject=AI%20Reserve%20account%20request";
const AUTH_INPUT_CLASS = "h-11 rounded-full border-white/65 bg-white/52 px-4 text-[#332b32] shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] placeholder:text-[#8f828b] focus-visible:border-[#6c2c59]/45 focus-visible:ring-[#6c2c59]/20 dark:border-white/10 dark:bg-white/[0.07] dark:text-[#f7f3f0] dark:placeholder:text-[#9f8f99]";
const AUTH_PRIMARY_CLASS = "h-11 rounded-full border border-[#48333f] bg-[#6c2c59] text-[#f7f7f7] shadow-[0_12px_28px_-18px_rgba(73,40,63,0.85),inset_0_1px_0_rgba(255,255,255,0.14)] hover:bg-[#763260]";
const AUTH_SECONDARY_CLASS = "h-11 rounded-full border-white/65 bg-white/42 text-[#493e46] shadow-[inset_0_1px_0_rgba(255,255,255,0.82)] hover:bg-white/60 hover:text-[#332b32] dark:border-white/10 dark:bg-white/[0.06] dark:text-[#e9e1e6] dark:hover:bg-white/[0.10] dark:hover:text-[#f7f3f0]";
export default function LoginPage() {
  const {
    user,
    isAuthenticating,
    authError,
    mfaChallenge,
    clearMfaChallenge
  } = useAuth();
  const searchParams = useSearchParams();
  const router = useRouter();
  // Only honor same-origin relative paths so ?redirect= can't bounce users
  // to an external site (open-redirect hardening, AIR-564).
  const explicitRedirect = sanitizeRedirectPath(searchParams.get("redirect"));
  const t = useTranslations("login");
  // Outcomes the /welcome link route hands to this page (2026-09-24). Only
  // the known codes render — the query string is untrusted.
  const linkError = searchParams.get("error");
  const linkNotice = searchParams.get("notice");
  const linkErrorMessage = linkError === "invalid_link" ? t("inviteInvalid") : linkError === "invite_expired" ? t("inviteExpired") : linkError === "link_expired" ? t("linkExpired") : null;
  const linkNoticeMessage = linkNotice === "invite_resent" ? t("inviteResent") : null;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);
  const [isSsoLoading, setIsSsoLoading] = useState(false);
  const [hasStartedAuth, setHasStartedAuth] = useState(false);
  const [view, setView] = useState<"signIn" | "resetPassword" | "resetEmailSent">("signIn");
  const [isResetLoading, setIsResetLoading] = useState(false);
  // Two-factor challenge (AIR-1500): Firebase stops a password/Google/
  // Microsoft sign-in with auth/multi-factor-auth-required when the account
  // has an authenticator app; the resolver finishes it with the app's code.
  // Popup/password sign-ins hand the resolver over right here; production
  // Google/Microsoft sign-ins return via redirect, so theirs surfaces in
  // AuthProvider's getRedirectResult and arrives through the auth context.
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const activeMfaResolver = mfaResolver ?? mfaChallenge;
  const activeView = activeMfaResolver ? "mfa" : view;
  const isBusy = isLoading || isGoogleLoading || isMicrosoftLoading || isSsoLoading;

  // If user is already logged in, redirect them. Deep links (?redirect=)
  // always win; otherwise land by role — super admins on the platform
  // admin dashboard, everyone else (enterprise admins included) on the
  // portal home. Mirrors defaultLandingPath, which cannot be
  // imported here: post-login-destination pulls in chat-host, whose
  // next/server import must stay out of client bundles.
  useEffect(() => {
    if (user) {
      router.push(explicitRedirect ?? (isSuperAdmin(user) ? "/admin" : "/home"));
    }
  }, [user, explicitRedirect, router]);

  // Reset loading states when user logs out and returns to login page
  useEffect(() => {
    if (!user && !isAuthenticating) {
      setIsLoading(false);
      setIsGoogleLoading(false);
      setIsMicrosoftLoading(false);
      setIsSsoLoading(false);
      setHasStartedAuth(false);
    }
  }, [user, isAuthenticating]);
  const getErrorMessage = (error_0: unknown) => {
    if (error_0 instanceof FirebaseError) {
      switch (error_0.code) {
        case "auth/user-not-found":
        case "auth/wrong-password":
        case "auth/invalid-credential":
          return "Invalid email or password. Please check your credentials and try again.";
        case "auth/invalid-email":
          return "The email address is invalid.";
        case "auth/too-many-requests":
          return "Too many failed attempts. Please try again later.";
        case "auth/account-exists-with-different-credential":
          return "An account already exists for this email with a different sign-in method. Sign in the way you did before, or contact support.";
        case "auth/admin-restricted-operation":
          return "Unable to sign in. Please contact support.";
        default:
          return "Unable to sign in. Please verify your account details or contact support if the issue persists.";
      }
    }
    return "An unexpected error occurred. Please try again.";
  };
  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setView("resetEmailSent");
    } catch (err: unknown) {
      // Don't reveal whether an account exists — show the same confirmation
      // as a successful send (email-enumeration hardening).
      if (err instanceof FirebaseError && err.code === "auth/user-not-found") {
        setView("resetEmailSent");
      } else {
        console.error("Password reset error:", err);
        setError(getErrorMessage(err));
      }
    } finally {
      setIsResetLoading(false);
    }
  };
  const switchView = (next: "signIn" | "resetPassword") => {
    setView(next);
    setError(null);
  };

  // The first factor passed; Firebase now wants the authenticator code.
  // Returns false when the error is anything else so the caller reports it.
  const beginMfaChallenge = (err_0: unknown): boolean => {
    if (!isFirebaseError(err_0, MFA_ERROR_CODES.multiFactorRequired)) return false;
    setMfaResolver(getMultiFactorResolver(auth, err_0 as MultiFactorError));
    setMfaCode("");
    setError(null);
    return true;
  };
  const handleMfaVerify = async (e_0: React.FormEvent) => {
    e_0.preventDefault();
    if (!activeMfaResolver || !isTotpCode(mfaCode)) return;
    const hint = activeMfaResolver.hints.find(factor => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID);
    if (!hint) {
      // Nothing this page can verify: drop back to the sign-in form with the
      // explanation instead of leaving a code box that can never succeed.
      setMfaResolver(null);
      clearMfaChallenge();
      setMfaCode("");
      setError(t("mfaNoTotp"));
      return;
    }
    setError(null);
    setIsVerifying(true);
    setHasStartedAuth(true);
    try {
      await activeMfaResolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(hint.uid, mfaCode));
      // AuthProvider will handle the redirect after syncing
      // Keep loading state - isAuthenticating will take over
    } catch (err_1: unknown) {
      console.error("Two-factor verification error:", err_1);
      setError(isFirebaseError(err_1, MFA_ERROR_CODES.invalidCode) ? t("mfaInvalidCode") : getErrorMessage(err_1));
      setMfaCode("");
      setIsVerifying(false);
      setHasStartedAuth(false);
    }
  };
  const handleMfaBack = () => {
    setMfaResolver(null);
    clearMfaChallenge();
    setMfaCode("");
    setError(null);
    setPassword("");
  };
  const handleEmailLogin = async (e_1: React.FormEvent) => {
    e_1.preventDefault();
    setError(null);
    setIsLoading(true);
    setHasStartedAuth(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // AuthProvider will handle the redirect after syncing
      // Keep loading state - isAuthenticating will take over
    } catch (err_2: unknown) {
      if (!beginMfaChallenge(err_2)) {
        console.error("Login error:", err_2);
        setError(getErrorMessage(err_2));
      }
      setIsLoading(false);
      setHasStartedAuth(false);
    }
  };
  const handleGoogleLogin = async () => {
    setError(null);
    setIsGoogleLoading(true);
    setHasStartedAuth(true);
    try {
      await signInWithGoogle();
      // AuthProvider will handle the redirect after syncing
    } catch (err_3: unknown) {
      if (!beginMfaChallenge(err_3)) {
        console.error("Google login error:", err_3);
        setError(getErrorMessage(err_3));
      }
      setIsGoogleLoading(false);
      setHasStartedAuth(false);
    }
  };
  const handleMicrosoftLogin = async () => {
    setError(null);
    setIsMicrosoftLoading(true);
    setHasStartedAuth(true);
    try {
      await signInWithMicrosoft();
      // AuthProvider will handle the redirect after syncing
    } catch (err_4: unknown) {
      if (!beginMfaChallenge(err_4)) {
        console.error("Microsoft login error:", err_4);
        setError(getErrorMessage(err_4));
      }
      setIsMicrosoftLoading(false);
      setHasStartedAuth(false);
    }
  };
  const handleSsoLogin = async () => {
    setError(null);
    // SSO routes by email domain — ask for the email (already the first
    // field on the form) instead of failing into a dead end.
    if (!email.trim()) {
      setError(t("ssoEmailRequired"));
      return;
    }
    setIsSsoLoading(true);
    try {
      const {
        providerId,
        unavailable
      } = await lookupSsoProvider(email);
      if (unavailable) {
        // Rate limit / transient failure — a "try again" message, never a
        // misleading "SSO isn't set up" for a configured customer.
        setError(t(unavailable === "rate_limited" ? "ssoRateLimited" : "ssoTryAgain"));
        setIsSsoLoading(false);
        return;
      }
      if (!providerId) {
        // Identical for unknown domains and orgs without SSO (no enumeration).
        setError(t("ssoNotAvailable"));
        setIsSsoLoading(false);
        return;
      }
      setHasStartedAuth(true);
      // No `finally` reset on purpose: after signInWithSSO the page is
      // navigating to the IdP, and the loading state must persist through
      // the redirect (a reset would flash the form back). Only the explicit
      // failure exits above/below re-enable the button.
      await signInWithSSO(providerId);
      // AuthProvider will handle the redirect after syncing
    } catch (err_5: unknown) {
      console.error("SSO login error:", err_5);
      setError(getErrorMessage(err_5));
      setIsSsoLoading(false);
      setHasStartedAuth(false);
    }
  };

  // Show loading screen during auth validation
  if (isAuthenticating || hasStartedAuth && (isBusy || isVerifying)) {
    return <AuthLoadingScreen message="Signing you in..." />;
  }
  return <AuthShell>
      <Card className="w-full max-w-[26rem] gap-0 rounded-[2rem] border-white/65 bg-white/46 py-0 text-[#332b32] shadow-[0_28px_80px_-38px_rgba(73,40,63,0.70),inset_0_1px_0_rgba(255,255,255,0.92)] backdrop-blur-[28px] dark:border-white/10 dark:bg-white/[0.065] dark:text-[#f7f3f0] dark:shadow-[0_30px_90px_-34px_rgba(0,0,0,0.82),inset_0_1px_0_rgba(255,255,255,0.12)]">
        <CardHeader className="gap-3 px-7 pt-7 pb-3 text-center">
          <div className="flex flex-col items-center gap-3">
            <span aria-hidden className="size-3 rotate-45 rounded-[3px] border border-[#48333f] bg-[#6c2c59] shadow-[0_5px_16px_rgba(108,44,89,0.40)]" />
            <h1 className="font-serif text-3xl leading-none font-medium tracking-[-0.01em]">AI Reserve</h1>
          </div>
          {activeView !== "signIn" ? <p className="max-w-xs text-sm leading-5 text-[#796a74] dark:text-[#b7a9b2]">
              {activeView === "mfa" ? t("mfaDescription") : t("resetSubtitle")}
            </p> : null}
        </CardHeader>

        <CardContent className="space-y-5 px-7 pt-3 pb-4">
          {/* Local form errors win; authError covers a sign-in that succeeded
              at Firebase but couldn't establish the server session (AIR-819). */}
          {(error ?? authError ?? linkErrorMessage) && <div className="rounded-2xl border border-[#6c2c59]/20 bg-[#6c2c59]/8 p-3 text-sm text-[#6c2c59] dark:text-[#dba3c9]">
              {error ?? authError ?? linkErrorMessage}
            </div>}
          {!error && !authError && linkNoticeMessage && <div className="rounded-2xl border border-[#6c2c59]/20 bg-[#6c2c59]/8 p-3 text-sm text-[#493e46] dark:text-[#e9e1e6]" role="status">
              {linkNoticeMessage}
            </div>}

          {activeView === "mfa" ? <form onSubmit={handleMfaVerify} className="space-y-4">
              <div className="space-y-1">
                <h2 className="text-base font-semibold">{t("mfaTitle")}</h2>
              </div>
              <TotpCodeInput id="login-totp-code" label={t("mfaCodeLabel")} value={mfaCode} onChange={setMfaCode} disabled={isVerifying} invalid={error !== null} autoFocus />
              <Button type="submit" className={`w-full ${AUTH_PRIMARY_CLASS}`} disabled={isVerifying || !isTotpCode(mfaCode)}>
                {isVerifying ? t("mfaVerifying") : t("mfaVerify")}
              </Button>
              <Button type="button" variant="link" className="w-full text-[#796a74] dark:text-[#b7a9b2]" onClick={handleMfaBack} disabled={isVerifying}>
                {t("mfaBack")}
              </Button>
            </form> : activeView !== "signIn" ? <div className="space-y-4">
              {view === "resetEmailSent" ? <div className="rounded-2xl border border-[#6c2c59]/20 bg-[#6c2c59]/8 p-3 text-sm">
                  {t("resetEmailSent")}
                </div> : <form onSubmit={handlePasswordReset} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">{t("emailLabel")}</Label>
                    <Input type="email" id="reset-email" placeholder={t("emailPlaceholder")} value={email} onChange={e_2 => setEmail(e_2.target.value)} required autoFocus disabled={isResetLoading} className={AUTH_INPUT_CLASS} />
                  </div>

                  <Button type="submit" className={`w-full ${AUTH_PRIMARY_CLASS}`} disabled={isResetLoading}>
                    {isResetLoading ? t("sendingResetLink") : t("sendResetLink")}
                  </Button>
                </form>}

              <Button type="button" variant="link" className="w-full text-[#796a74] dark:text-[#b7a9b2]" onClick={() => switchView("signIn")} disabled={isResetLoading}>
                {t("backToSignIn")}
              </Button>
            </div> : <>
            <form onSubmit={handleEmailLogin} className="space-y-3.5">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="px-1 text-xs font-medium text-[#665962] dark:text-[#c7bac2]">
                  {t("emailLabel")}
                </Label>
                <Input type="email" id="email" placeholder={t("emailPlaceholder")} value={email} onChange={e_3 => setEmail(e_3.target.value)} required disabled={isBusy} autoComplete="email" className={AUTH_INPUT_CLASS} />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="px-1 text-xs font-medium text-[#665962] dark:text-[#c7bac2]">
                    {t("passwordLabel")}
                  </Label>
                  <button type="button" onClick={() => switchView("resetPassword")} className="text-xs text-[#796a74] underline-offset-4 transition-colors hover:text-[#332b32] hover:underline dark:text-[#b7a9b2] dark:hover:text-[#f7f3f0]" disabled={isBusy}>
                    {t("forgotPassword")}
                  </button>
                </div>
                <Input type="password" id="password" placeholder={t("passwordPlaceholder")} value={password} onChange={e_4 => setPassword(e_4.target.value)} required disabled={isBusy} autoComplete="current-password" className={AUTH_INPUT_CLASS} />
              </div>

              <Button type="submit" className={`w-full ${AUTH_PRIMARY_CLASS}`} disabled={isBusy}>
                {isLoading ? t("signingIn") : t("signInWithEmail")}
              </Button>
            </form>

            <div className="flex items-center gap-3" aria-label={t("orContinueWith")}>
              <div className="h-px flex-1 bg-[#796a74]/18 dark:bg-white/10" />
              <span className="text-xs text-[#796a74] dark:text-[#b7a9b2]">or</span>
              <div className="h-px flex-1 bg-[#796a74]/18 dark:bg-white/10" />
            </div>

            <Button type="button" variant="outline" className={`w-full ${AUTH_SECONDARY_CLASS}`} onClick={handleGoogleLogin} disabled={isBusy}>
              {isGoogleLoading ? t("signingIn") : <>
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                  </svg>
                  Sign in with Google
                </>}
            </Button>

            <Button type="button" variant="outline" className={`w-full ${AUTH_SECONDARY_CLASS}`} onClick={handleSsoLogin} disabled={isBusy}>
              {isSsoLoading ? t("signingIn") : <>
                  <KeyRound className="mr-2 h-4 w-4" aria-hidden="true" />
                  {t("continueWithSso")}
                </>}
            </Button>

            {false && <Button type="button" variant="outline" className="w-full" onClick={handleMicrosoftLogin} disabled={isBusy}>
              {isMicrosoftLoading ? t("signingIn") : <>
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 23 23" aria-hidden="true">
                    <path fill="#f25022" d="M1 1h10v10H1z" />
                    <path fill="#00a4ef" d="M12 1h10v10H12z" />
                    <path fill="#7fba00" d="M1 12h10v10H1z" />
                    <path fill="#ffb900" d="M12 12h10v10H12z" />
                  </svg>
                  Sign in with Microsoft
                </>}
            </Button>}
            </>}
        </CardContent>

        <CardFooter className="flex-col px-7 pt-0 pb-6">
          <p className="text-center text-xs text-[#796a74] dark:text-[#b7a9b2]">
            {t.rich("noAccount", {
            contact: chunks => <a href={ACCOUNT_REQUEST_HREF} className="font-medium text-[#493e46] underline underline-offset-4 hover:text-[#6c2c59] dark:text-[#e9e1e6] dark:hover:text-[#dba3c9]">
                  {chunks}
                </a>
          })}
          </p>
        </CardFooter>
      </Card>
      <LegalFooter className="w-full max-w-md shrink-0 px-4 py-4 text-center text-[8px] leading-tight text-[#796a74]/65 dark:text-[#b7a9b2]/65" />
    </AuthShell>;
}